/**
 * MAIN-845: what the Import drawer tells a caterer when an import cannot start.
 *
 * The API already words these failures for caterers (for example
 * MENU_IMPORT_INVALID_SOURCE_URL: "That EZ Cater link uses an unexpected port
 * and was not used."). The drawer was replacing them. CREATE_MENU_IMPORT_JOB
 * had an onError option, and with one Apollo's mutate() resolves to
 * { data: undefined, errors } instead of rejecting. The drawer then found no
 * job id, threw "Import did not return a job id." and that toast replaced the
 * server's message about half a second after it appeared. The upload-URL step
 * had the same shape: a lazy query resolves with { error } rather than
 * rejecting, so its reason became "Could not get an upload URL for this file."
 */

export const IMPORT_START_FALLBACK_MESSAGE = "Could not start the import. Please try again.";

/** An import-start failure whose message is already written for the caterer. */
export class MenuImportStartError extends Error {
  constructor(message) {
    super(message);
    this.name = "MenuImportStartError";
  }
}

function firstMessage(errors) {
  if (!Array.isArray(errors)) return "";
  const found = errors.find((error) => typeof error?.message === "string" && error.message.trim());
  return found ? found.message.trim() : "";
}

/**
 * The message to show for a failed import start. A GraphQL error from the API
 * wins: it is the specific reason, already worded for the caterer.
 */
export function getImportStartErrorMessage(error) {
  if (!error) return IMPORT_START_FALLBACK_MESSAGE;
  const serverMessage =
    firstMessage(error.graphQLErrors) || firstMessage(error.networkError?.result?.errors);
  if (serverMessage) return serverMessage;
  return error.message || IMPORT_START_FALLBACK_MESSAGE;
}

/** Apollo reports a failure as an ApolloError or as a raw GraphQL errors array. */
function toError(errors) {
  return Array.isArray(errors) ? { graphQLErrors: errors } : errors;
}

/**
 * The signed upload target from a GET_MENU_IMPORT_UPLOAD_URL lazy-query
 * result, or a MenuImportStartError carrying the server's reason.
 */
export function getUploadTargetOrThrow(result) {
  const error = result?.error || (result?.errors ? toError(result.errors) : null);
  if (error) throw new MenuImportStartError(getImportStartErrorMessage(error));
  const target = result?.data?.getMenuImportUploadUrl;
  if (!target?.signedUrl) {
    throw new MenuImportStartError("Could not get an upload URL for this file.");
  }
  return target;
}

/**
 * Runs CREATE_MENU_IMPORT_JOB and returns the new job's id, or throws a
 * MenuImportStartError carrying the message to show. Both shapes a failed
 * Apollo mutate can take are handled — a rejection, or a resolved { errors }
 * when an onError option is set — so the server's reason survives either way.
 */
export async function createMenuImportJobId(mutate, variables) {
  let result;
  try {
    result = await mutate({ variables });
  } catch (error) {
    throw new MenuImportStartError(getImportStartErrorMessage(error));
  }
  if (result?.errors) {
    throw new MenuImportStartError(getImportStartErrorMessage(toError(result.errors)));
  }
  const jobId = result?.data?.createMenuImportJob?._id;
  if (!jobId) throw new MenuImportStartError(IMPORT_START_FALLBACK_MESSAGE);
  return jobId;
}
